import { 
  FaWarehouse, 
  FaToilet, 
  FaShip, 
  FaRecycle, 
  FaBuilding, 
  FaCubes 
} from 'react-icons/fa';
import { resolveIcon } from '@/sanity/iconMap';
import type { AboutSectionData } from '@/sanity/types';

// R2 - Service item structure
interface ServiceItem {
  title: string;
  icon: React.ReactNode;
}

// R2 - Services data with icons
const SERVICES: ServiceItem[] = [
  {
    title: 'Alquiler de módulos prefabricados',
    icon: <FaWarehouse className="h-6 w-6" />,
  },
  {
    title: 'Suministro de conjuntos modulares',
    icon: <FaCubes className="h-6 w-6" />,
  },
  {
    title: 'Alquiler de baños químicos',
    icon: <FaToilet className="h-6 w-6" />,
  },
  {
    title: 'Venta y alquiler de contenedores marítimos',
    icon: <FaShip className="h-6 w-6" />,
  },
  {
    title: 'Compra y venta de módulos prefabricados',
    icon: <FaBuilding className="h-6 w-6" />,
  },
  {
    title: 'Reacondicionamiento y rehabilitación de módulos usados',
    icon: <FaRecycle className="h-6 w-6" />,
  },
];

interface AboutServicesSectionProps {
  data?: AboutSectionData | undefined;
}

/**
 * R2 - Services grid section for About Us page
 * R4 - Accepts optional Sanity data, falls back to hardcoded defaults
 */
export function AboutServicesSection({ data }: AboutServicesSectionProps) {
  const badge = data?.servicesBadge ?? 'Especialistas en soluciones modulares';
  const sectionTitle = data?.servicesTitle ?? 'Nuestros Servicios';
  const sectionDesc = data?.servicesDescription ?? 'Nos especializamos en ofrecer soluciones integrales para cada proyecto';

  const resolvedServices: ServiceItem[] = data?.servicesItems?.length
    ? data.servicesItems.map(item => ({
        title: item.title,
        icon: resolveIcon(item.iconName, 'h-6 w-6'),
      }))
    : SERVICES;
  return (
    <section className="relative py-20 sm:py-32 bg-inboca-gray">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 sm:mb-20 animate-fade-in-up">
          <span className="inline-block px-4 py-2 bg-inboca-teal/10 rounded-full text-inboca-teal font-semibold text-sm mb-6">
            {badge}
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            {sectionTitle}
          </h2>
          <p className="text-lg sm:text-xl text-gray-700 max-w-2xl mx-auto leading-relaxed">
            {sectionDesc}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {resolvedServices.map((service, index) => (
            <div
              key={service.title}
              className="bg-white rounded-xl p-6 shadow-md border border-gray-100 card-lift-hover animate-fade-in-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-14 w-14 rounded-lg bg-gradient-to-br from-inboca-teal/10 to-inboca-orange/10 text-inboca-teal">
                    {service.icon}
                  </div>
                </div>
                <h3 className="text-lg font-bold text-gray-900 leading-snug">
                  {service.title}
                </h3>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
